import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, LogOut, Newspaper, Calendar, Star, Image, Video } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

function toDateInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("news");
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [media, setMedia] = useState([]);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const loadAll = () => {
    api.get("/news").then((r) => setNews(r.data));
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/media").then((r) => setMedia(r.data));
  };

  useEffect(() => {
    if (user) loadAll();
  }, [user]);

  if (loading) return <div className="pt-28 text-center">Cargando…</div>;
  if (!user) return <Navigate to="/admin" replace />;

  const openNew = (type) => {
    setEditing({ type, item: null });
    if (type === "news") setForm({ title: "", summary: "", content: "", image_url: "" });
    else if (type === "events")
      setForm({ title: "", description: "", location: "", event_date: "", image_url: "", featured: false });
    else setForm({ type: "photo", url: "", caption: "" });
  };

  const openEdit = (type, item) => {
    setEditing({ type, item });
    setForm({
      ...item,
      event_date: type === "events" ? toDateInput(item.event_date) : undefined,
    });
  };

  const closeForm = () => {
    setEditing(null);
    setForm({});
  };

  const save = async () => {
    try {
      if (editing.type === "news") {
        const payload = {
          title: form.title,
          summary: form.summary,
          content: form.content,
          image_url: form.image_url || null,
        };
        if (editing.item) {
          await api.put(`/news/${editing.item.id}`, payload);
          toast.success("Noticia actualizada");
        } else {
          await api.post("/news", payload);
          toast.success("Noticia creada");
        }
      } else if (editing.type === "events") {
        const payload = {
          title: form.title,
          description: form.description,
          location: form.location,
          event_date: new Date(form.event_date).toISOString(),
          image_url: form.image_url || null,
          featured: !!form.featured,
        };
        if (editing.item) {
          await api.put(`/events/${editing.item.id}`, payload);
          toast.success("Evento actualizado");
        } else {
          await api.post("/events", payload);
          toast.success("Evento creado");
        }
      } else {
        // media: create only (no edit endpoint)
        const payload = {
          type: form.type,
          url: form.url,
          caption: form.caption || null,
        };
        await api.post("/media", payload);
        toast.success("Agregado a la galería");
      }
      closeForm();
      loadAll();
    } catch (err) {
      toast.error("Error al guardar");
    }
  };

  const remove = async (type, id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    await api.delete(`/${type}/${id}`);
    toast.success("Eliminado");
    loadAll();
  };

  const tabs = [
    { key: "news", label: "Noticias", icon: Newspaper, count: news.length },
    { key: "events", label: "Eventos", icon: Calendar, count: events.length },
    { key: "media", label: "Galería", icon: Image, count: media.length },
  ];

  return (
    <div data-testid="admin-dashboard" className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--inv-text-muted)] mb-2">
              Panel
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight">
              Gestión de contenido
            </h1>
            <p className="text-sm text-[color:var(--inv-text-muted)] mt-1">
              Sesión: {user.email}
            </p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={() => {
              logout();
              navigate("/admin");
            }}
            className="btn-pill btn-pill-secondary text-sm"
          >
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>

        <div className="flex gap-2 border-b border-warm overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === t.key
                  ? "border-[color:var(--inv-primary)] text-[color:var(--inv-primary)]"
                  : "border-transparent text-[color:var(--inv-text-muted)]"
              }`}
            >
              <t.icon size={16} className="inline mr-2" /> {t.label} ({t.count})
            </button>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            data-testid={`add-${tab}-btn`}
            onClick={() => openNew(tab)}
            className="btn-pill btn-pill-primary text-sm"
          >
            <Plus size={16} />{" "}
            {tab === "news" ? "Nueva noticia" : tab === "events" ? "Nuevo evento" : "Agregar foto/video"}
          </button>
        </div>

        <div className="mt-6 bg-surface border border-warm rounded-2xl overflow-hidden">
          {tab === "news" && (
            <table className="w-full text-left">
              <thead className="bg-surface-alt text-xs uppercase tracking-wider text-[color:var(--inv-text-muted)]">
                <tr>
                  <th className="px-6 py-4">Título</th>
                  <th className="px-6 py-4">Resumen</th>
                  <th className="px-6 py-4">Publicado</th>
                  <th className="px-6 py-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {news.map((n) => (
                  <tr key={n.id} className="border-t border-warm">
                    <td className="px-6 py-4 font-medium">{n.title}</td>
                    <td className="px-6 py-4 text-sm text-[color:var(--inv-text-muted)] max-w-md">
                      <div className="line-clamp-2">{n.summary}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {new Date(n.published_at).toLocaleDateString("es-ES")}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <button
                        data-testid={`edit-news-${n.id}`}
                        onClick={() => openEdit("news", n)}
                        className="p-2 hover:bg-surface-alt rounded-lg transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        data-testid={`delete-news-${n.id}`}
                        onClick={() => remove("news", n.id)}
                        className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {news.length === 0 && (
                  <tr><td colSpan="4" className="px-6 py-10 text-center text-[color:var(--inv-text-muted)]">Sin noticias</td></tr>
                )}
              </tbody>
            </table>
          )}

          {tab === "events" && (
            <table className="w-full text-left">
              <thead className="bg-surface-alt text-xs uppercase tracking-wider text-[color:var(--inv-text-muted)]">
                <tr>
                  <th className="px-6 py-4">Título</th>
                  <th className="px-6 py-4">Fecha</th>
                  <th className="px-6 py-4">Ubicación</th>
                  <th className="px-6 py-4">Destacado</th>
                  <th className="px-6 py-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id} className="border-t border-warm">
                    <td className="px-6 py-4 font-medium">{ev.title}</td>
                    <td className="px-6 py-4 text-sm">
                      {new Date(ev.event_date).toLocaleString("es-ES")}
                    </td>
                    <td className="px-6 py-4 text-sm">{ev.location}</td>
                    <td className="px-6 py-4">
                      {ev.featured && <Star size={16} className="text-[color:var(--inv-primary)] fill-current" />}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <button
                        data-testid={`edit-event-${ev.id}`}
                        onClick={() => openEdit("events", ev)}
                        className="p-2 hover:bg-surface-alt rounded-lg transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        data-testid={`delete-event-${ev.id}`}
                        onClick={() => remove("events", ev.id)}
                        className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr><td colSpan="5" className="px-6 py-10 text-center text-[color:var(--inv-text-muted)]">Sin eventos</td></tr>
                )}
              </tbody>
            </table>
          )}

          {tab === "media" && (
            <div className="p-6">
              {media.length === 0 ? (
                <div className="py-10 text-center text-[color:var(--inv-text-muted)]">
                  Aún no has agregado fotos ni videos.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {media.map((m) => (
                    <div key={m.id} className="relative aspect-square rounded-xl overflow-hidden bg-surface-alt group">
                      <img
                        src={
                          m.type === "video"
                            ? `https://img.youtube.com/vi/${(m.url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([a-zA-Z0-9_-]{6,})/) || [])[1] || ""}/hqdefault.jpg`
                            : m.url
                        }
                        alt={m.caption || ""}
                        className="w-full h-full object-cover"
                      />
                      {m.type === "video" && (
                        <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 rounded">
                          <Video size={10} className="inline mr-1" /> Video
                        </div>
                      )}
                      <button
                        data-testid={`delete-media-${m.id}`}
                        onClick={() => remove("media", m.id)}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <Trash2 size={14} />
                      </button>
                      {m.caption && (
                        <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 to-transparent text-white text-xs">
                          {m.caption}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form modal */}
        {editing && (
          <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={closeForm}
          >
            <div
              data-testid="admin-form-modal"
              className="bg-surface rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8">
                <h3 className="font-heading text-2xl font-semibold tracking-tight">
                  {editing.item ? "Editar" : "Crear"}{" "}
                  {editing.type === "news"
                    ? "noticia"
                    : editing.type === "events"
                    ? "evento"
                    : "media"}
                </h3>

                <div className="mt-6 space-y-4">
                  {editing.type !== "media" && (
                    <div>
                      <label className="text-sm font-medium block mb-1.5">Título</label>
                      <input
                        data-testid="form-title"
                        value={form.title || ""}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-xl bg-warm border border-warm focus:outline-none focus:border-[color:var(--inv-primary)]"
                      />
                    </div>
                  )}

                  {editing.type === "news" && (
                    <>
                      <div>
                        <label className="text-sm font-medium block mb-1.5">Resumen</label>
                        <input
                          data-testid="form-summary"
                          value={form.summary || ""}
                          onChange={(e) => setForm({ ...form, summary: e.target.value })}
                          className="w-full px-4 py-2.5 rounded-xl bg-warm border border-warm focus:outline-none focus:border-[color:var(--inv-primary)]"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium block mb-1.5">Contenido</label>
                        <textarea
                          data-testid="form-content"
                          value={form.content || ""}
                          onChange={(e) => setForm({ ...form, content: e.target.value })}
                          rows={6}
                          className="w-full px-4 py-2.5 rounded-xl bg-warm border border-warm focus:outline-none focus:border-[color:var(--inv-primary)]"
                        />
                      </div>
                    </>
                  )}

                  {editing.type === "events" && (
                    <>
                      <div>
                        <label className="text-sm font-medium block mb-1.5">Descripción</label>
                        <textarea
                          data-testid="form-description"
                          value={form.description || ""}
                          onChange={(e) => setForm({ ...form, description: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-xl bg-warm border border-warm focus:outline-none focus:border-[color:var(--inv-primary)]"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium block mb-1.5">Fecha y hora</label>
                          <input
                            data-testid="form-event-date"
                            type="datetime-local"
                            value={form.event_date || ""}
                            onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl bg-warm border border-warm focus:outline-none focus:border-[color:var(--inv-primary)]"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium block mb-1.5">Ubicación</label>
                          <input
                            data-testid="form-location"
                            value={form.location || ""}
                            onChange={(e) => setForm({ ...form, location: e.target.value })}
                            className="w-full px-4 py-2.5 rounded-xl bg-warm border border-warm focus:outline-none focus:border-[color:var(--inv-primary)]"
                          />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          data-testid="form-featured"
                          type="checkbox"
                          checked={!!form.featured}
                          onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                          className="accent-[color:var(--inv-primary)]"
                        />
                        Marcar como evento destacado (aparece arriba)
                      </label>
                    </>
                  )}

                  {editing.type === "media" && (
                    <>
                      <div>
                        <label className="text-sm font-medium block mb-1.5">Tipo</label>
                        <div className="flex gap-2">
                          {["photo", "video"].map((t) => (
                            <button
                              key={t}
                              type="button"
                              data-testid={`media-type-${t}`}
                              onClick={() => setForm({ ...form, type: t })}
                              className={`btn-pill text-sm ${
                                form.type === t ? "btn-pill-primary" : "btn-pill-secondary"
                              }`}
                            >
                              {t === "photo" ? "Foto" : "Video (YouTube)"}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium block mb-1.5">
                          {form.type === "video" ? "URL de YouTube" : "URL de imagen"}
                        </label>
                        <input
                          data-testid="form-media-url"
                          value={form.url || ""}
                          onChange={(e) => setForm({ ...form, url: e.target.value })}
                          placeholder={
                            form.type === "video"
                              ? "https://www.youtube.com/watch?v=..."
                              : "https://...imagen.jpg"
                          }
                          className="w-full px-4 py-2.5 rounded-xl bg-warm border border-warm focus:outline-none focus:border-[color:var(--inv-primary)]"
                        />
                        {form.type === "photo" && (
                          <p className="text-xs text-[color:var(--inv-text-muted)] mt-1">
                            Sube la foto a Imgur, Google Photos compartido, o cualquier sitio
                            público y pega el enlace aquí.
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  <div>
                    <label className="text-sm font-medium block mb-1.5">
                      {editing.type === "media" ? "Descripción (opcional)" : "URL de imagen (opcional)"}
                    </label>
                    <input
                      data-testid={editing.type === "media" ? "form-media-caption" : "form-image-url"}
                      value={editing.type === "media" ? form.caption || "" : form.image_url || ""}
                      onChange={(e) =>
                        editing.type === "media"
                          ? setForm({ ...form, caption: e.target.value })
                          : setForm({ ...form, image_url: e.target.value })
                      }
                      placeholder={editing.type === "media" ? "Bautismo de Juan…" : "https://..."}
                      className="w-full px-4 py-2.5 rounded-xl bg-warm border border-warm focus:outline-none focus:border-[color:var(--inv-primary)]"
                    />
                  </div>
                </div>

                <div className="mt-8 flex justify-end gap-3">
                  <button
                    data-testid="form-cancel"
                    onClick={closeForm}
                    className="btn-pill btn-pill-secondary text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    data-testid="form-save"
                    onClick={save}
                    className="btn-pill btn-pill-primary text-sm"
                  >
                    Guardar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
